import {
    categoryCodeToReadableTag,
    CLASS_EVENTBRITE_FORMAT,
    DATING_EVENTBRITE_SUB_CATEGORY,
    EVENTBRITE_FORMAT_MAPPING_PLURAL,
    FOOD_AND_DRINK_EVENTBRITE_CATEGORY,
    NIGHTLIFE_EVENTBRITE_CATEGORY,
    PARTY_EVENTBRITE_FORMAT,
    PERFORMANCE_EVENTBRITE_FORMAT,
} from '@eventbrite/categories';
import { CoreUser } from '@eventbrite/context-gen';
import { gettext } from '@eventbrite/i18n';
import loadable from '@loadable/component';
import { Location } from 'history';
import React from 'react';
import { DateRange } from '../../../constants/dates';
import { PaidEventsCarousel } from '../../../containers/PaidEventsCarousel';
import { CollectionTouchpoint } from '../components/CollectionTouchpoint';
import { FREE, MOST_POPULAR, THIS_WEEKEND } from '../constants/bucketKey';
import {
    COMPONENTS_LABELS,
    COMPONENTS_ORDER,
} from '../constants/categoryCustomConfig';
import { CategoryBrowseEvents } from '../containers/CategoryBrowseEvents';
import { useSearchContext } from '../context';
import {
    CATEGORY_BROWSE_PLACEMENT,
    NIGHTLIFE_CATEGORY_BROWSE_PLACEMENT,
} from './analytics';

const CategoryBrowseDynamicFormatEvents = loadable(
    () => import('../containers/CategoryBrowseDynamicFormatEvents'),
    {
        resolveComponent: (components: {
            CategoryBrowseDynamicFormatEvents: React.FunctionComponent;
        }) => components.CategoryBrowseDynamicFormatEvents,
    },
);

const TrendingTagEvents = loadable(
    () => import('../containers/TrendingTagEvents'),
    {
        resolveComponent: (components: {
            TrendingTagEvents: React.FunctionComponent;
        }) => components.TrendingTagEvents,
    },
);

const CategoryBrowseRelatedCategories = loadable(
    () => import('../containers/CategoryBrowseRelatedCategories'),
    {
        resolveComponent: (components: {
            CategoryBrowseRelatedCategories: React.FunctionComponent;
        }) => components.CategoryBrowseRelatedCategories,
    },
);

const CategoryBar = loadable(
    () => import('../containers/CategoryBar/CategoryBar'),
    {
        resolveComponent: (components: {
            CategoryBar: React.FunctionComponent;
        }) => components.CategoryBar,
    },
);

const NightlifeCategoriesContainer = loadable(
    () => import('../containers/NightlifeCategoriesContainer'),
    {
        resolveComponent: (components: {
            NightlifeCategoriesContainer: React.FunctionComponent;
        }) => components.NightlifeCategoriesContainer,
    },
);

const CategoryBrowseOrganizers = loadable(
    () => import('../containers/CategoryBrowseOrganizers'),
);

const CategoryBrowseOrganizerTags = loadable(
    () => import('../containers/CategoryBrowseOrganizerTags'),
);

const CategoryBrowseNearbyCities = loadable(
    () => import('../containers/CategoryBrowseNearbyCities'),
);

const CategoryBrowsePopularCategories = loadable(
    () => import('../containers/CategoryBrowsePopularCategories'),
    {
        resolveComponent: (components: {
            CategoryBrowsePopularCategories: React.FunctionComponent;
        }) => components.CategoryBrowsePopularCategories,
    },
);

const CategoryBrowsePopularFormats = loadable(
    () => import('../containers/CategoryBrowsePopularFormats'),
);

const CategoryBrowsePopularTimes = loadable(
    () => import('../containers/CategoryBrowsePopularTimes'),
);

interface CarouselSequenceProps {
    activeCategory: string;
    date: string | undefined;
    customDate: DateRange | undefined;
    location: Location<string> | undefined;
    locationData: any;
    locale?: string;
    isMobile?: boolean;
    user: CoreUser | undefined;
    isBotRequest: boolean;
    placeId: string;
    currentPlace: string;
    tld: string;
    enableSeasonalModule: boolean;
    enableLighthouseCreatorsModule: boolean;
}

export const CarouselSequence = (props: CarouselSequenceProps) => {
    const { query } = useSearchContext();
    const extraQuery = query.q ? query.q : '';
    const isNightlifePage =
        props.activeCategory === NIGHTLIFE_EVENTBRITE_CATEGORY;
    const bucketName = isNightlifePage
        ? NIGHTLIFE_CATEGORY_BROWSE_PLACEMENT
        : CATEGORY_BROWSE_PLACEMENT;
    const nearbyCitiesClass = isNightlifePage ? 'near_by_cities' : '';

    const CATEGORY_BROWSE_COMPONENTS: {
        [key: string]: any;
    } = {
        [COMPONENTS_LABELS.categoryTab]: {
            component: CategoryBrowseRelatedCategories,
        },
        [COMPONENTS_LABELS.categoryBar]: {
            component: CategoryBar,
            params: {},
        },
        [COMPONENTS_LABELS.nightlifeCategories]: {
            component: NightlifeCategoriesContainer,
            params: {},
        },
        [COMPONENTS_LABELS.mostPopular]: {
            component: CategoryBrowseEvents,
            params: {
                queryOverload: {
                    page_size: 25,
                    q: extraQuery,
                    tags_or: query.tags ? query.tags : [],
                    tags: [],
                },
                bucketName: bucketName,
                bucketKey: MOST_POPULAR,
                bucketPosition: '1',
                renderEmptyState: true,
                adSlots: [1, 4],
            },
        },
        [COMPONENTS_LABELS.weekend]: {
            component: CategoryBrowseEvents,
            params: {
                queryOverload: {
                    page_size: 25,
                    q: extraQuery,
                    dates: ['this_weekend'],
                    aggs: {},
                },
                bucketName: bucketName,
                bucketKey: THIS_WEEKEND,
                title: gettext('This weekend'),
                bucketPosition: '2',
                adSlots: [1],
            },
            renderCondition:
                props.date === 'current_future' &&
                !props.customDate &&
                query.dates &&
                !query.dates.includes('this_weekend'),
        },
        [COMPONENTS_LABELS.organizerToFollow]: {
            component: CategoryBrowseOrganizers,
            params: {
                user: props.user,
                location: props.location,
            },
        },
        [COMPONENTS_LABELS.nightlife]: {
            component: CategoryBrowseEvents,
            params: {
                queryOverload: {
                    page_size: 25,
                    start_hour_range: {
                        end_hour: 24,
                        start_hour: 20,
                    },
                },
                title: gettext('After 8pm'),
                bucketName: bucketName,
                bucketKey: NIGHTLIFE_EVENTBRITE_CATEGORY,
                bucketPosition: '1',
            },
        },
        [COMPONENTS_LABELS.parties]: {
            component: CategoryBrowseEvents,
            params: {
                queryOverload: {
                    q: extraQuery,
                    tags: [
                        PARTY_EVENTBRITE_FORMAT,
                        DATING_EVENTBRITE_SUB_CATEGORY != props.activeCategory
                            ? props.activeCategory
                            : '',
                    ],
                    page_size: 25,
                },
                title: gettext('Parties'),
                bucketName: bucketName,
                bucketKey:
                    categoryCodeToReadableTag(PARTY_EVENTBRITE_FORMAT) ||
                    PARTY_EVENTBRITE_FORMAT,
                bucketPosition: '1',
            },
            renderCondition: !(
                query?.tags &&
                query.tags.length == 1 &&
                query.tags.includes(PARTY_EVENTBRITE_FORMAT)
            ),
        },

        [COMPONENTS_LABELS.budgetFriendly]: {
            component: PaidEventsCarousel,
            params: {
                isAuthenticated: props.user?.isAuthenticated,
                locale: props.locale,
                page: props.activeCategory,
                isMobile: props.isMobile,
                affCode: 'ebdssbcategorybrowse',
                location: props.locationData,
            },
        },
        [COMPONENTS_LABELS.dynamicEvents0]: {
            component: CategoryBrowseDynamicFormatEvents,
            params: {
                bucketName: bucketName,
                bucketNumber: 0,
                bucketPosition: '5',
            },
        },
        [COMPONENTS_LABELS.free]: {
            component: CategoryBrowseEvents,
            params: {
                queryOverload: {
                    page_size: 25,
                    q: extraQuery,
                    price: 'free',
                    aggs: {},
                },
                title: gettext('Free'),
                bucketName: bucketName,
                bucketKey: FREE,
                bucketPosition: '6',
                adSlots: [1],
                forceRenderHeader: props.isBotRequest,
            },
        },
        [COMPONENTS_LABELS.trendingTags]: {
            component: TrendingTagEvents,
            params: {
                title: gettext('Trending Tags'),
                bucketName: bucketName,
                bucketKey: 'trending_tag_events',
                bucketPosition: '7',
                placeId: props.placeId,
                isNightlife: isNightlifePage,
            },
        },
        [COMPONENTS_LABELS.dynamicEvents1]: {
            component: CategoryBrowseDynamicFormatEvents,
            params: {
                bucketName: bucketName,
                bucketNumber: 1,
                bucketPosition: '8',
            },
        },
        [COMPONENTS_LABELS.dynamicEvents2]: {
            component: CategoryBrowseDynamicFormatEvents,
            params: {
                bucketName: bucketName,
                bucketNumber: 2,
                bucketPosition: '9',
            },
        },
        [COMPONENTS_LABELS.popularFormats]: {
            component: CategoryBrowsePopularFormats,
        },
        [COMPONENTS_LABELS.popularTimes]: {
            component: CategoryBrowsePopularTimes,
        },
        [COMPONENTS_LABELS.popularCategories]: {
            component: CategoryBrowsePopularCategories,
            params: { enableSeasonalModule: props.enableSeasonalModule },
        },
        [COMPONENTS_LABELS.nearbyCities]: {
            component: CategoryBrowseNearbyCities,
            params: {
                customClassName: nearbyCitiesClass,
            },
        },
        [COMPONENTS_LABELS.OrganizersTags]: {
            component: CategoryBrowseOrganizerTags,
            key: COMPONENTS_LABELS.OrganizersTags,
        },
        [COMPONENTS_LABELS.meetSomeoneNew]: {
            component: CategoryBrowseEvents,
            params: {
                queryOverload: {
                    q: 'singles' + ' ' + extraQuery,
                    price: 'paid',
                    aggs: {},
                    page_size: 25,
                },
                bucketName: bucketName,
                bucketKey: 'meet_someone_new',
                title: gettext('Meet someone new'),
                bucketPosition: '8',
            },
        },
        [COMPONENTS_LABELS.planDate]: {
            component: CategoryBrowseEvents,
            params: {
                queryOverload: {
                    q: 'couples' + ' ' + extraQuery,
                    price: 'paid',
                    aggs: {},
                    page_size: 25,
                },
                bucketName: bucketName,
                bucketKey: 'plan_date',
                title: gettext('Plan a date'),
                bucketPosition: '9',
            },
        },
        [COMPONENTS_LABELS.foodAndDrink]: {
            component: CategoryBrowseEvents,
            params: {
                queryOverload: {
                    page_size: 25,
                    q: extraQuery,
                    aggs: {},
                    price: 'paid',
                    tags: [FOOD_AND_DRINK_EVENTBRITE_CATEGORY],
                },
                bucketName: bucketName,
                bucketKey: 'food_and_drink',
                title: gettext('Food And Drink'),
                bucketPosition: '10',
            },
        },
        [COMPONENTS_LABELS.fineArts]: {
            component: CategoryBrowseEvents,
            params: {
                queryOverload: {
                    page_size: 25,
                    q: extraQuery,
                    tags: [PERFORMANCE_EVENTBRITE_FORMAT],
                    price: 'paid',
                    aggs: {},
                },
                title: EVENTBRITE_FORMAT_MAPPING_PLURAL[
                    PERFORMANCE_EVENTBRITE_FORMAT
                ],
                bucketKey:
                    categoryCodeToReadableTag(PERFORMANCE_EVENTBRITE_FORMAT) ||
                    PERFORMANCE_EVENTBRITE_FORMAT,
                bucketName: bucketName,
                bucketPosition: '9',
            },
        },
        [COMPONENTS_LABELS.classes]: {
            component: CategoryBrowseEvents,
            params: {
                queryOverload: {
                    q: extraQuery,
                    tags: [CLASS_EVENTBRITE_FORMAT],
                },
                title: gettext('Classes'),
                bucketName: bucketName,
                bucketKey:
                    categoryCodeToReadableTag(CLASS_EVENTBRITE_FORMAT) ||
                    CLASS_EVENTBRITE_FORMAT,
                bucketPosition: '1',
            },
        },
        [COMPONENTS_LABELS.collectionTouchpoint]: {
            component: CollectionTouchpoint,
            params: {
                placeId: props.placeId,
                activeCategory: props.activeCategory,
                currentPlace: props.currentPlace,
            },
        },
    };

    const renderOrder =
        COMPONENTS_ORDER[props.activeCategory] || COMPONENTS_ORDER.default;

    const components: any[] = [];
    renderOrder.forEach((componentKey) => {
        const componentData = CATEGORY_BROWSE_COMPONENTS[componentKey];
        if (componentData) {
            components.push(componentData);
        }
    });
    return components;
};
